import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { ButtonComponent } from '@bcef/ia-components/buttons';
import { Listbox2Component, Listbox2OptionComponent } from '@bcef/ia-components/listbox';
import { ContextService } from '@bcef/ia-shared';
import { TranslatePipe } from '@ngx-translate/core';
import { AttachmentComponent } from 'src/app/components/attachment/attachment.component';
import { FieldErrorComponent } from 'src/app/components/field-error.component';
import { PanelComponent } from 'src/app/layouts/panel.components';

import { acceptAttachments } from '@core/converters/attachment-policy.converter';
import { ProblemDescription } from '@core/models/problem-description.model';
import { ReportStore } from '@core/store/report.store';

import { createDescriptionForm } from './description.form';

@Component({
  selector: 'ia-report-technical-problem-description',
  templateUrl: './description.component.html',
  imports: [
    TranslatePipe,
    Listbox2Component,
    Listbox2OptionComponent,
    FormField,
    ButtonComponent,
    AttachmentComponent,
    PanelComponent,
    FieldErrorComponent,
  ],
  host: {
    class: 'tw-flex tw-flex-col tw-space-y-5',
  },
})
export class DescriptionComponent {
  protected readonly store = inject(ReportStore);

  protected readonly context = inject(ContextService);

  constructor() {
    effect(() => {
      this.store.setTheme(this.form.theme().value());
      this.store.setDescription(this.form.description().value());
    });
  }

  protected readonly model = signal<ProblemDescription>(this.store.description());

  protected readonly form = createDescriptionForm(
    this.model,
    this.store.config().descriptionMaxLength,
  );

  protected readonly remainingCharacters = computed(
    () => this.store.config().descriptionMaxLength - this.form.description().value().length,
  );

  protected readonly canAddMoreAttachment = computed(
    () =>
      this.store.description().attachments.length < this.store.config().attachmentPolicy.maxCount,
  );

  protected readonly maxAttachments = computed(() => this.store.config().attachmentPolicy.maxCount);

  protected onAttachmentsSelected(event: Event): void {
    this.form.attachments().value.set([]);
    const input = event.target as HTMLInputElement;
    const files = input.files ?? [];
    this.form.attachments().value.update((current) => [...current, ...files]);

    const accepted = acceptAttachments(
      Array.from(input.files ?? []),
      this.store.config().attachmentPolicy,
      this.store.description().attachments.length,
    );

    input.value = '';
    accepted.map((file) => this.store.addAttachment(file));
    this.form.attachments().markAsTouched();
  }

  protected onContinue(): void {
    if (!this.valid()) return;
    this.store.goToStep(2);
  }

  public valid = computed(() => this.form.description().valid() && this.form.theme().valid());
}
